import {
  Box,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  Button,
  Text,
  HStack,
  TableContainer,
} from '@chakra-ui/react';
import { Rock, User } from '../../types';

interface RockSummaryTableProps {
  rocks: Rock[];
  users: User[];
  onIssueClick: (rockId: string) => void;
  refreshTrigger?: number;
}

/**
 * Rock Summary Table Component
 * Displays rocks in a summary table format
 * Migrated from Tailwind to Chakra UI
 */
export function RockSummaryTable({
  rocks,
  users,
  onIssueClick,
  refreshTrigger = 0,
}: RockSummaryTableProps) {
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'on-track':
        return 'green';
      case 'off-track':
        return 'red';
      default:
        return 'gray';
    }
  };

  const formatDate = (date: Date | string) => {
    if (date instanceof Date) {
      return date.toLocaleDateString();
    }
    return new Date(date).toLocaleDateString();
  };

  if (rocks.length === 0) {
    return (
      <Box textAlign="center" py={12} bg="white" borderRadius="lg" border="1px" borderColor="gray.200">
        <Text color="gray.500" fontSize="lg" mb={2}>
          No rocks found
        </Text>
      </Box>
    );
  }

  return (
    <Box bg="white" rounded="lg" border="1px" borderColor="gray.200" overflow="hidden">
      <TableContainer>
        <Table variant="simple" size="sm">
          <Thead bg="gray.50">
            <Tr>
              <Th px={6} py={3} textAlign="left" fontSize="xs" fontWeight="medium" color="gray.500" textTransform="uppercase" letterSpacing="wider">
                Owner
              </Th>
              <Th px={6} py={3} textAlign="left" fontSize="xs" fontWeight="medium" color="gray.500" textTransform="uppercase" letterSpacing="wider">
                Rock
              </Th>
              <Th px={6} py={3} textAlign="left" fontSize="xs" fontWeight="medium" color="gray.500" textTransform="uppercase" letterSpacing="wider">
                Status
              </Th>
              <Th px={6} py={3} textAlign="left" fontSize="xs" fontWeight="medium" color="gray.500" textTransform="uppercase" letterSpacing="wider">
                Progress
              </Th>
              <Th px={6} py={3} textAlign="left" fontSize="xs" fontWeight="medium" color="gray.500" textTransform="uppercase" letterSpacing="wider">
                Due Date
              </Th>
              <Th px={6} py={3} textAlign="left" fontSize="xs" fontWeight="medium" color="gray.500" textTransform="uppercase" letterSpacing="wider">
                Actions
              </Th>
            </Tr>
          </Thead>
          <Tbody>
            {rocks.map((rock) => {
              const owner = users.find((u) => u.id === rock.ownerId);
              return (
                <Tr
                  key={rock.id}
                  id={`rock-${rock.id}`}
                  _hover={{ bg: 'gray.50' }}
                  borderLeft={rock.status === 'off-track' ? '4px solid' : 'none'}
                  borderLeftColor={rock.status === 'off-track' ? 'red.500' : 'transparent'}
                  bg={rock.status === 'off-track' ? 'red.50' : 'white'}
                >
                  {/* Owner */}
                  <Td px={6} py={4} whiteSpace="nowrap">
                    <Badge colorScheme="blue" px={2} py={1} fontSize="xs" fontWeight="medium" borderRadius="full">
                      {owner?.name || 'Unknown'}
                    </Badge>
                  </Td>

                  {/* Rock Name */}
                  <Td px={6} py={4}>
                    <Text fontSize="sm" color="gray.900" fontWeight="medium">
                      {rock.rock}
                    </Text>
                    <Text fontSize="xs" color="gray.600" mt={1}>
                      {rock.measurable}
                    </Text>
                  </Td>

                  {/* Status */}
                  <Td px={6} py={4} whiteSpace="nowrap">
                    <Badge
                      colorScheme={getStatusColor(rock.status)}
                      px={2.5}
                      py={0.5}
                      fontSize="xs"
                      fontWeight="medium"
                      borderRadius="full"
                    >
                      {rock.status === 'on-track' ? 'On Track' : 'Off Track'}
                    </Badge>
                  </Td>

                  {/* Progress */}
                  <Td px={6} py={4} whiteSpace="nowrap">
                    <Text fontSize="sm" fontWeight="medium" color="gray.900">
                      {rock.progress}%
                    </Text>
                  </Td>

                  {/* Due Date */}
                  <Td px={6} py={4} whiteSpace="nowrap">
                    <Text fontSize="sm" color="gray.600">
                      {formatDate(rock.dueDate)}
                    </Text>
                  </Td>

                  {/* Actions */}
                  <Td px={6} py={4} whiteSpace="nowrap">
                    <Button
                      size="sm"
                      variant="link"
                      colorScheme="blue"
                      onClick={() => onIssueClick(rock.id)}
                    >
                      View Issues
                    </Button>
                  </Td>
                </Tr>
              );
            })}
          </Tbody>
        </Table>
      </TableContainer>
    </Box>
  );
}

